import React from 'react';
import Box from '@mui/material/Box';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useAdminTeamsQuery } from '../../api/admin';
import AdminGridLink from './AdminGridLink';
import AdminQuickFilterToolbar from './AdminQuickFilterToolbar';

interface Props {
	/** When set, only teams in this organization are listed and the Organization column is hidden. */
	organizationId?: number;
	/** When set, only these teams are listed (an account's covered teams). */
	teamIds?: number[];
	height?: string | number;
}

const organizationColumn: GridColDef = {
	field: 'organization_name',
	headerName: 'Organization',
	flex: 1,
	minWidth: 160,
	renderCell: (params: GridRenderCellParams) => (
		<AdminGridLink
			to={`/admin/organizations/${params.row.organization_id}`}
			state={{ organizationName: params.value }}
		>
			{params.value}
		</AdminGridLink>
	),
};

export default function TeamsTable({ organizationId, teamIds, height = 'calc(100vh - 310px)' }: Props) {
	const { data: teams, isLoading } = useAdminTeamsQuery(organizationId);
	const rows = teamIds ? (teams ?? []).filter((t) => teamIds.includes(t.id)) : teams ?? [];

	const columns: GridColDef[] = [
		{
			field: 'name',
			headerName: 'Team',
			flex: 1.5,
			minWidth: 200,
			renderCell: (params: GridRenderCellParams) => (
				<AdminGridLink
					to={`/admin/teams/${params.row.id}`}
					state={{ teamName: params.row.name, organizationName: params.row.organization_name }}
				>
					{params.value}
				</AdminGridLink>
			),
		},
		...(organizationId === undefined ? [organizationColumn] : []),
		{ field: 'member_count', headerName: 'Members', width: 100, type: 'number' },
		{
			field: 'description',
			headerName: 'Description',
			flex: 2,
			minWidth: 160,
		},
		{
			field: 'created_at',
			headerName: 'Created',
			width: 120,
			valueFormatter: ({ value }) => (value ? new Date(value).toLocaleDateString('en-CA') : ''),
		},
	];

	return (
		<Box sx={{ height, width: '100%' }}>
			<DataGrid
				rows={rows}
				columns={columns}
				loading={isLoading}
				density="compact"
				disableSelectionOnClick
				components={{ Toolbar: AdminQuickFilterToolbar }}
				componentsProps={{ toolbar: { showQuickFilter: true } }}
			/>
		</Box>
	);
}
